import React, { useEffect, useMemo, useState, useRef } from "react";
import { parseToLocalFixedHour } from "../../../utils/toDateTimeLocal";
import PMInterventionActionPanel from "./PMInterventionActionPanel";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Line,
  Scatter,
  ReferenceLine,
  LabelList,
} from "recharts";
import ToolTipComp from "../../layouts/ToolTip";

// Custom shape renderer for larger scatter dots
const CustomScatterDot = (props) => {
  const { cx, cy, fill, stroke, strokeWidth, isHighlighted, highlightColor } =
    props;
  const radius = isHighlighted ? 10 : 3.5;
  const fillColor = isHighlighted
    ? highlightColor || "#FFFFFF"
    : fill || "#FFFFFF";
  const strokeColor = isHighlighted ? "#FFFFFF" : stroke || "#000";
  const strokeW = isHighlighted ? 3 : strokeWidth || 2;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill={fillColor}
      stroke={strokeColor}
      strokeWidth={strokeW}
    />
  );
};

// Custom renderer for goal labels (shows value in a rounded tag above the point)
const renderGoalLabel = (props) => {
  const { x, y, value } = props;
  const text = String(value);
  const textWidth = Math.max(24, text.length * 8); // approximate
  const rectWidth = textWidth + 12;
  const rectHeight = 20;
  const rectX = x - rectWidth / 2;
  const rectY = y - rectHeight - 18; // place a bit higher than the point

  return (
    <g>
      <rect
        x={rectX}
        y={rectY}
        width={rectWidth}
        height={rectHeight}
        rx={10}
        ry={10}
        fill="#FFFFFF"
        stroke="#000000"
        strokeWidth={1}
      />
      <text
        x={x}
        y={rectY + rectHeight / 2 + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        style={{ fontSize: 12, fontWeight: 700, fill: "#000" }}
      >
        {text}
      </text>
    </g>
  );
};
// Level color mapping
const LEVEL_COLORS = {
  1: "#B91C1C",
  2: "#E5533D",
  3: "#F39C12",
  4: "#F4D03F",
  5: "#A3D977",
  6: "#5FBF7A",
  7: "#2FB7A0",
  8: "#18A587",
};

const getLevelColor = (levelId) => {
  return LEVEL_COLORS[levelId] || "#999999";
};
const PMChart = ({
  setGoalSettings,
  data,
  groupKey,
  globalChartDomain,
  goalID,
  interventions,
}) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [placementMode, setPlacementMode] = useState(false);
  const [interventionsState, setInterventionsState] = useState([]);
  const chartRef = useRef(null);
  const [hoverPreview, setHoverPreview] = useState(null); // { leftPx, time }
  const [selectedInterventionId, setSelectedInterventionId] = useState(null);

  // Fixed plotting range in days (changeable). Chart shows a constant time window centered on data.
  const PLOTTING_RANGE_DAYS = 365; // one year window by default
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const CHART_MARGIN = { top: 30, right: 40, left: 40, bottom: 20 };

  const chartTitle = useMemo(() => {
    if (!data || data.length === 0) return null;

    const { AssessmentType, LevelID: levelFromData, ResultRef } = data[0];
    // skill extraction: handle both object and JSON-string ResultRef
    let skillType = null;
    try {
      if (ResultRef) {
        const parsed =
          typeof ResultRef === "string" ? JSON.parse(ResultRef) : ResultRef;
        skillType = parsed?.AssessmentSkill ?? parsed?.Skill ?? null;
      }
    } catch (err) {
      // ignore parse errors
    }

    // Prefer LevelID from the grouping key (passed as prop) if available
    let resolvedLevel = levelFromData;
    try {
      if (groupKey && typeof groupKey === "string") {
        const parts = groupKey.split("_");
        if (parts.length > 0 && parts[0] !== "" && !isNaN(Number(parts[0]))) {
          resolvedLevel = Number(parts[0]);
        }
      }
    } catch (err) {
      // ignore
    }

    return {
      AssessmentType,
      LevelID: resolvedLevel,
      SkillType: skillType,
    };
  }, [data, groupKey]);

  // Normalize incoming data to ensure consistent field names and types
  const normalizedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((d) => {
      // try common goal id/name variants and ResultID/id
      let goalId = d.goalId ?? d.GoalID ?? d.GoalId ?? d.Goalid ?? null;
      // attempt to parse ResultRef if goal not present
      if (!goalId && d.ResultRef) {
        try {
          const parsed = JSON.parse(d.ResultRef);
          goalId = parsed?.GoalID ?? parsed?.goalId ?? parsed?.goalID ?? null;
        } catch (err) {
          // ignore
        }
      }
      return {
        ...d,
        goalId,
      };
    });
  }, [data]);

  // cancel placement on Escape key when placementMode active
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && placementMode) setPlacementMode(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [placementMode]);

  const scatterData = useMemo(() => {
    if (!normalizedData || normalizedData.length === 0) return [];
    return normalizedData.map((d) => ({
      ...d,
      time: parseToLocalFixedHour(d.CreatedDate, 19),
    }));
  }, [normalizedData]);

  const scatterPlotData = useMemo(() => {
    return scatterData.slice(1);
  }, [scatterData]);

  const highlightTime = useMemo(() => {
    if (!scatterData || scatterData.length === 0) return null;
    return Math.max(...scatterData.map((d) => d.time));
  }, [scatterData]);

  const levelColor = getLevelColor(chartTitle?.LevelID);

  const goalLineData = useMemo(() => {
    if (!normalizedData || normalizedData.length === 0) return [];
    const first = normalizedData[0];
    const goal = first?.goalId;
    if (!goal || goal === "") return [];
    return [
      {
        time: parseToLocalFixedHour(first.StartDate, 19),
        TotalScore: first.TotalScore ?? first.BaseScore,
      },
      {
        time: parseToLocalFixedHour(first.EndDate, 19),
        TotalScore: first.TargetScore,
      },
    ];
  }, [normalizedData]);

  // A stable key representing the identity of the incoming results set
  const dataKey = useMemo(() => {
    if (!normalizedData || normalizedData.length === 0) return "";
    return normalizedData
      .map((d) => String(d.ResultID ?? d.id ?? d.CreatedDate ?? d.time ?? ""))
      .join("|");
  }, [normalizedData]);

  // When the incoming set of points changes (add/remove), sync derived local state
  useEffect(() => {
    if (!normalizedData || normalizedData.length === 0) return;

    // Ensure interventions list includes any intervention date declared on the dataset
    const ivDate = normalizedData[0]?.InterventionDate;
    if (ivDate) {
      const t = parseToLocalFixedHour(ivDate, 19);
      setInterventionsState((s) => {
        if (s.find((it) => it.time === t)) return s;
        return [
          ...s,
          {
            id: `existing-${t}`,
            time: t,
            actionText: normalizedData[0].InterventionAction || "Intervention",
            note: normalizedData[0].InterventionNote || "",
            provisional: false,
          },
        ];
      });
    }

    // If selected intervention came from a removed point, clear selection
    setSelectedInterventionId((sel) => {
      if (!sel) return sel;
      // check if any intervention still matches selected id
      if (interventionsState.find((it) => String(it.id) === String(sel)))
        return sel;
      return null;
    });
  }, [dataKey]);

  // Initialize interventions state from data - follows same pattern as goal line parsing
  useEffect(() => {
    // Parse Interventions from normalizedData array (consistent with goal line approach)
    if (!normalizedData || normalizedData.length === 0) {
      setInterventionsState([]);
      return;
    }

    try {
      const allInterventions = [];

      // Iterate through all data items and collect interventions (same iteration pattern as goal check)
      normalizedData.forEach((item) => {
        const interventionsJson = item?.Interventions;

        if (interventionsJson) {
          // Parse the JSON string (handle both string and object, like ResultRef parsing)
          const interventionsArray =
            typeof interventionsJson === "string"
              ? JSON.parse(interventionsJson)
              : interventionsJson;

          if (
            Array.isArray(interventionsArray) &&
            interventionsArray.length > 0
          ) {
            // Map API fields to component format - use parseToLocalFixedHour(time, 19) like goal line
            interventionsArray.forEach((intervention) => {
              allInterventions.push({
                id: intervention.InterventionDetailID,
                time: parseToLocalFixedHour(intervention.InterventionTime, 19),
                actionText: intervention.InterventionAction || "Intervention",
                note: intervention.InterventionNotes || "",
                provisional: false,
              });
            });
          }
        }
      });

      // Remove duplicates by ID (in case same intervention appears on multiple results)
      const uniqueInterventions = allInterventions.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.id === item.id),
      );

      setInterventionsState(uniqueInterventions);
    } catch (err) {
      console.error("Failed to parse Interventions from API", err);
      setInterventionsState([]);
    }
  }, [normalizedData]);

  const combinedData = [...(scatterData || []), ...goalLineData];

  const chartDomain = useMemo(() => {
    if (!combinedData || combinedData.length === 0)
      return ["dataMin", "dataMax"];
    const times = combinedData
      .map((d) => d && d.time)
      .filter((t) => typeof t === "number");

    // Include intervention times from state in domain calculation
    const interventionTimes = (interventionsState || [])
      .map((it) => it && it.time)
      .filter((t) => typeof t === "number");

    const allTimes = [...times, ...interventionTimes];

    if (allTimes.length === 0) return ["dataMin", "dataMax"];
    const minT = Math.min(...allTimes);
    const maxT = Math.max(...allTimes);
    const center = Math.round((minT + maxT) / 2);
    const halfRange = Math.round((PLOTTING_RANGE_DAYS * MS_PER_DAY) / 2);
    return [center - halfRange, center + halfRange];
  }, [combinedData, interventionsState]);

  // allow parent to override per-card domain so multiple charts can share the same X window
  const effectiveChartDomain =
    globalChartDomain &&
    Array.isArray(globalChartDomain) &&
    globalChartDomain.length === 2
      ? globalChartDomain
      : chartDomain;

  // Compute X axis quarterly groups (labels like "Dec 25 - Feb 26") and tick centers
  const xGroups = useMemo(() => {
    if (
      !effectiveChartDomain ||
      !Array.isArray(effectiveChartDomain) ||
      effectiveChartDomain.length !== 2
    )
      return null;
    const [d0, d1] = effectiveChartDomain;
    if (typeof d0 !== "number" || typeof d1 !== "number") return null;
    const start = new Date(d0);
    const end = new Date(d1);
    const groups = [];
    let cur = new Date(start);
    // Ensure loop makes progress even if day-of-month causes month rollover
    while (cur.getTime() < end.getTime()) {
      const groupStart = new Date(cur);
      const groupEnd = new Date(groupStart);
      groupEnd.setMonth(groupEnd.getMonth() + 3);
      if (groupEnd.getTime() > end.getTime()) groupEnd.setTime(end.getTime());
      const label = `${groupStart.toLocaleString(undefined, { month: "short" })} ${groupStart.getDate()} - ${groupEnd.toLocaleString(undefined, { month: "short" })} ${groupEnd.getDate()}`;
      const center = Math.round(
        (groupStart.getTime() + groupEnd.getTime()) / 2,
      );
      groups.push({
        start: groupStart.getTime(),
        end: groupEnd.getTime(),
        center,
        label,
      });
      // advance to groupEnd; add one ms to avoid infinite loop when groupEnd==cur
      cur = new Date(groupEnd.getTime() + 1);
    }
    const ticks = groups.map((g) => g.center);
    const labelMap = {};
    groups.forEach((g) => {
      labelMap[g.center] = g.label;
    });
    return { ticks, labelMap };
  }, [effectiveChartDomain]);

  // Consistent readable date formatter
  const formatDate = (t) => {
    try {
      const d = new Date(t);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (err) {
      return "";
    }
  };

  // Custom X axis tick renderer that draws a grey rounded tab behind the label
  const CustomXAxisTick = ({ x, y, payload, labelMap }) => {
    try {
      const raw = payload && payload.value ? payload.value : null;
      const txt =
        labelMap && raw && labelMap[raw]
          ? labelMap[raw]
          : raw
            ? formatDate(raw)
            : "";
      // split into two lines only when intervention panel is expanded
      const parts =
        panelOpen && typeof txt === "string" && txt.includes(" - ")
          ? txt.split(" - ")
          : [txt];
      const lineHeight = 14;
      const maxLineLength = Math.max(...parts.map((s) => (s ? s.length : 8)));
      const rectWidth = Math.max(72, maxLineLength * 7 + 24);
      const rectHeight = Math.max(22, parts.length * lineHeight + 12);
      const rectX = x - rectWidth / 2;
      const rectY = y - rectHeight / 2 + 8; // nudge down so tabs sit near axis line

      // compute starting y for first line so lines are vertically centered
      const startY =
        rectY + rectHeight / 2 - ((parts.length - 1) * lineHeight) / 2 + 1;

      return (
        <g>
          <rect
            x={rectX}
            y={rectY}
            width={rectWidth}
            height={rectHeight}
            rx={10}
            ry={10}
            fill="#f4f6f8"
            stroke="#e6e9ec"
          />
          {parts.map((line, i) => (
            <text
              key={i}
              x={x}
              y={startY + i * lineHeight}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: 12, fill: "#6b6b6b", fontWeight: 600 }}
            >
              {line}
            </text>
          ))}
        </g>
      );
    } catch (err) {
      return null;
    }
  };

  // Custom tooltip styled like other dashboard charts (black label bar + colored bullets)
  const PMCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((acc, p) => acc + (p.value || 0), 0);
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "white",
            padding: "12px",
            borderRadius: 6,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <p
            className="label"
            style={{
              backgroundColor: "#000",
              color: "#fff",
              padding: "6px 10px",
              borderRadius: 4,
              textAlign: "center",
              margin: 0,
              marginBottom: 8,
              fontSize: 12,
            }}
          >
            {label ? formatDate(label) : ""}
          </p>

          {payload.map((p) => {
            const name = p.name || p.dataKey || "value";
            // try to derive a color: use provided color, otherwise try payload props
            const color =
              p.color ||
              (p.payload && (p.payload.fill || p.payload.stroke)) ||
              "#000";
            const circleStyle = {
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: color,
              display: "inline-block",
              marginRight: "8px",
            };
            return (
              <p
                key={name}
                style={{
                  color: "#111",
                  margin: 0,
                  marginBottom: 6,
                  fontSize: 13,
                }}
              >
                <span style={circleStyle}></span>
                {`${name}: ${p.value}`}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Compute Y axis domain with buffer so top labels/dots don't get clipped
  const yDomain = useMemo(() => {
    if (!combinedData || combinedData.length === 0)
      return ["dataMin", "dataMax"];
    const scores = combinedData
      .map((d) => (d && typeof d.TotalScore === "number" ? d.TotalScore : null))
      .filter((s) => typeof s === "number");
    if (!scores || scores.length === 0) return [0, "dataMax"];
    const minS = Math.min(...scores, 0);
    const maxS = Math.max(...scores);
    // buffer: at least 5 units or 10% of max, whichever larger
    const buffer = Math.max(5, Math.ceil(maxS * 0.1));
    return [minS, maxS + buffer];
  }, [combinedData]);

  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  return (
    <div className="w-100 mb-2 p-2">
      {/* Heading */}
      <div className="w-100 row">
        <div className="d-flex align-items-center justify-content-between">
          {/* Title */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            {/* Assessment name — primary heading */}
            <span
              style={{
                fontSize: "1.05rem",
                fontWeight: 700,
                color: "#1a1a1a",
                lineHeight: 1.2,
              }}
            >
              {chartTitle?.AssessmentType}
            </span>

            {/* Skill badge — only shown when meaningful */}
            {chartTitle?.SkillType && chartTitle?.SkillType !== "N/A" && (
              <span
                style={{
                  display: "inline-block",
                  background: "#F3F4F6",
                  color: "#6B7280",
                  border: "1px solid #D1D5DB",
                  padding: "2px 10px",
                  borderRadius: "20px",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {chartTitle?.SkillType}
              </span>
            )}

            {/* Level badge */}
            <span
              style={{
                display: "inline-block",
                background: getLevelColor(chartTitle?.LevelID),
                color: "#FFFFFF",
                padding: "2px 10px",
                borderRadius: "20px",
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Level {chartTitle?.LevelID}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              alignItems: "flex-end",
            }}
          >
            <ToolTipComp
              myButton={
                <button
                  className="pm-action-btn"
                  onClick={() => setGoalSettings({ groupKey, data })}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="20px"
                    viewBox="0 -960 960 960"
                    width="20px"
                    fill="currentColor"
                  >
                    <path d="M331-126q-70-30-122.5-82.5T126-331q-30-70-30-149.5t30-149q30-69.5 82.5-122T331-834q70-30 149.5-30t149 30q69.5 30 122 82.5t82.5 122q30 69.5 30 149T834-331q-30 70-82.5 122.5t-122 82.5q-69.5 30-149 30T331-126Zm370-133q91-91 91-221t-91-221q-91-91-221-91t-221 91q-91 91-91 221t91 221q91 91 221 91t221-91Zm-391-51q-70-70-70-170t70-170q70-70 170-70t170 70q70 70 70 170t-70 170q-70 70-170 70t-170-70Zm289-51q49-49 49-119t-49-119q-49-49-119-49t-119 49q-49 49-49 119t49 119q49 49 119 49t119-49Zm-170-68q-21-21-21-51t21-51q21-21 51-21t51 21q21 21 21 51t-21 51q-21 21-51 21t-51-21Z" />
                  </svg>
                </button>
              }
              type="Goal"
              module="settings"
            />

            <ToolTipComp
              myButton={
                <button
                  className={`pm-action-btn ${panelOpen ? "close" : ""}`}
                  onClick={() => setPanelOpen((p) => !p)}
                  aria-expanded={panelOpen}
                >
                  {panelOpen ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
                    </svg>
                  )}
                </button>
              }
              type={panelOpen ? "Close" : "Open"}
              module={"intervention"}
            />
          </div>
        </div>
      </div>

      {/*Body  */}
      <div className="w-100 row">
        {/* Chart Section */}
        <div className={`d-flex flex-column ${panelOpen ? "col-8" : "col-12"}`}>
          <div className="">
            {/* Chart Area */}
            <div ref={chartRef} style={{ position: "relative" }}>
              <ResponsiveContainer width="100%" height={420}>
                <ComposedChart
                  data={combinedData}
                  margin={{ top: 30, right: 40, left: 40, bottom: 20 }}
                  style={{ overflow: "visible" }}
                >
                  <CartesianGrid stroke="#eee" />

                  {/* X = DateTime */}
                  <XAxis
                    type="number"
                    dataKey="time"
                    domain={effectiveChartDomain}
                    ticks={xGroups?.ticks}
                    tick={(props) => (
                      <CustomXAxisTick
                        {...props}
                        labelMap={xGroups ? xGroups.labelMap : null}
                      />
                    )}
                    interval={0}
                  />

                  {/* Y = Score */}
                  <YAxis type="number" dataKey="TotalScore" domain={yDomain} />

                  <Tooltip content={PMCustomTooltip} />

                  {/* Goal line */}
                  {goalLineData.length > 0 && (
                    <Line
                      data={goalLineData}
                      type="linear"
                      dataKey="TotalScore"
                      stroke="#000000"
                      // custom dot renderer to style base (start) and target (end) differently
                      dot={(props) => {
                        const { cx, cy, index, payload, value } = props;
                        if (index === 0) {
                          // base score: solid black filled circle
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={6}
                              fill="#000000"
                              stroke="#000000"
                            />
                          );
                        }
                        // target score: white-filled ring with inner colored dot
                        return (
                          <g>
                            <circle
                              cx={cx}
                              cy={cy}
                              r={10}
                              fill="#FFFFFF"
                              stroke="#000000"
                              strokeWidth={2}
                            />
                            <circle
                              cx={cx}
                              cy={cy}
                              r={4}
                              fill={levelColor}
                              stroke={levelColor}
                            />
                            {renderGoalLabel({
                              x: cx,
                              y: cy,
                              value:
                                value != null
                                  ? value
                                  : payload && payload.TotalScore,
                            })}
                          </g>
                        );
                      }}
                      isAnimationActive={false}
                    >
                      {/* Render label directly inside target dot so it stays aligned with the marker */}
                    </Line>
                  )}

                  {/* Render interventions from local state as reference lines */}
                  {interventionsState.map((it) => (
                    <ReferenceLine
                      key={it.id}
                      x={it.time}
                      stroke="#6b6b6b"
                      strokeDasharray={it.provisional ? "4 4" : "6 6"}
                      label={null}
                    />
                  ))}

                  {/* Scatter points  */}
                  {scatterPlotData.length > 0 && (
                    <Scatter
                      data={scatterPlotData}
                      fill="#FFFFFF"
                      stroke="#000"
                      strokeWidth={2}
                      shape={
                        <CustomScatterDot
                          fill="#FFFFFF"
                          stroke="#000"
                          strokeWidth={0.7}
                        />
                      }
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>

              {/* click overlay for placement mode */}
              {placementMode && (
                <div
                  onClick={(e) => {
                    try {
                      const container = chartRef.current;
                      if (!container) return;
                      const svg = container.querySelector("svg");
                      const svgRect = svg
                        ? svg.getBoundingClientRect()
                        : container.getBoundingClientRect();
                      const leftOffset = svgRect.left + CHART_MARGIN.left;
                      const innerWidth = Math.max(
                        1,
                        svgRect.width - CHART_MARGIN.left - CHART_MARGIN.right,
                      );
                      let x = e.clientX - leftOffset;
                      x = Math.min(innerWidth, Math.max(0, x));

                      const domain = effectiveChartDomain;
                      const min = Number(domain[0]);
                      const max = Number(domain[1]);
                      if (isNaN(min) || isNaN(max)) return;
                      const ratio = Math.min(1, Math.max(0, x / innerWidth));
                      const time = Math.round(min + ratio * (max - min));
                      const newIt = {
                        id: `tmp-${Date.now()}`,
                        time,
                        actionText: "",
                        note: "",
                        provisional: true,
                      };
                      setInterventionsState((s) => [...s, newIt]);
                      setPlacementMode(false);
                      setPanelOpen(true);
                      setHoverPreview(null);
                    } catch (err) {
                      console.error("placement click error", err);
                    }
                  }}
                  onMouseMove={(e) => {
                    try {
                      const container = chartRef.current;
                      if (!container) return;
                      const svg = container.querySelector("svg");
                      const svgRect = svg
                        ? svg.getBoundingClientRect()
                        : container.getBoundingClientRect();
                      const leftOffset = svgRect.left + CHART_MARGIN.left;
                      const innerWidth = Math.max(
                        1,
                        svgRect.width - CHART_MARGIN.left - CHART_MARGIN.right,
                      );
                      let x = e.clientX - leftOffset;
                      if (x < 0 || x > innerWidth) return setHoverPreview(null);
                      const domain = effectiveChartDomain;
                      const min = Number(domain[0]);
                      const max = Number(domain[1]);
                      if (isNaN(min) || isNaN(max))
                        return setHoverPreview(null);
                      const ratio = Math.min(1, Math.max(0, x / innerWidth));
                      const time = Math.round(min + ratio * (max - min));
                      setHoverPreview({ leftPx: e.clientX, time });
                    } catch (err) {
                      setHoverPreview(null);
                    }
                  }}
                  onMouseLeave={() => setHoverPreview(null)}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    cursor: "crosshair",
                    zIndex: 10,
                  }}
                />
              )}

              {/* hover preview when in placement mode */}
              {placementMode &&
                hoverPreview &&
                chartRef.current &&
                (() => {
                  const container = chartRef.current;
                  const svg = container.querySelector("svg");
                  const svgRect = svg
                    ? svg.getBoundingClientRect()
                    : container.getBoundingClientRect();
                  const label = formatDate(hoverPreview.time);
                  const previewWidth = 120;
                  const left = Math.min(
                    svgRect.right - previewWidth - 8,
                    Math.max(svgRect.left + 8, hoverPreview.leftPx),
                  );
                  const previewStyle = {
                    position: "absolute",
                    left: left - container.getBoundingClientRect().left,
                    top: 8,
                    background: "#000",
                    color: "#fff",
                    padding: "6px 8px",
                    borderRadius: 6,
                    fontSize: 12,
                    pointerEvents: "none",
                    zIndex: 20,
                  };
                  return <div style={previewStyle}>{label}</div>;
                })()}

              {/* Overlay markers (pills) for interventionsState */}
              {chartRef.current && interventionsState.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: "none",
                  }}
                >
                  {(() => {
                    const rect = chartRef.current.getBoundingClientRect();
                    const w = rect.width || 1;
                    const domain = effectiveChartDomain;
                    const min = Number(domain[0]);
                    const max = Number(domain[1]);
                    if (isNaN(min) || isNaN(max)) return null;
                    return interventionsState.map((it) => {
                      const ratio = Math.min(
                        1,
                        Math.max(0, (it.time - min) / (max - min)),
                      );
                      const leftPct = ratio * 100;
                      const topPx = 36; // place near top inside chart
                      const pillStyle = {
                        position: "absolute",
                        left: `${leftPct}%`,
                        top: topPx,
                        transform: "translateX(-50%)",
                        background: it.provisional ? "#fff7e6" : "#ffffff",
                        border: `1px solid ${it.provisional ? "#f39c12" : "#e6e9ec"}`,
                        padding: "6px 8px",
                        borderRadius: 12,
                        fontSize: 12,
                        color: "#222",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        pointerEvents: "auto",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      };
                      return (
                        <div
                          key={it.id}
                          style={{
                            position: "absolute",
                            left: `${leftPct}%`,
                            top: topPx,
                            transform: "translateX(-50%)",
                            pointerEvents: "auto",
                          }}
                        >
                          <div
                            role="button"
                            tabIndex={0}
                            aria-pressed={selectedInterventionId === it.id}
                            aria-label={`${it.provisional ? "Provisional" : "Saved"} intervention on ${new Date(it.time).toLocaleString()}: ${it.actionText || ""}`}
                            title={
                              it.actionText ||
                              (it.provisional
                                ? "New intervention"
                                : "Intervention")
                            }
                            style={pillStyle}
                            onClick={() => {
                              setSelectedInterventionId(it.id);
                              setPanelOpen(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedInterventionId(it.id);
                                setPanelOpen(true);
                              }
                            }}
                          >
                            {it.actionText && it.actionText.length > 0
                              ? it.actionText
                              : it.provisional
                                ? "New intervention"
                                : "Intervention"}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Intervention Action Panel */}
        {panelOpen && (
          <div className="d-flex flex-column col-4">
            <PMInterventionActionPanel
              isOpen={panelOpen}
              placementMode={placementMode}
              onStartPlacementToggle={() => setPlacementMode((p) => !p)}
              onCancelPlacement={() => setPlacementMode(false)}
              selectedInterventionId={selectedInterventionId}
              interventions={interventionsState}
              resultIDs={
                normalizedData && normalizedData.length > 0
                  ? normalizedData.map((d) => d.ResultID).filter(Boolean)
                  : []
              }
              onSaveIntervention={(id, payload) => {
                setInterventionsState((s) =>
                  s.map((it) =>
                    it.id === id
                      ? { ...it, ...payload, provisional: false }
                      : it,
                  ),
                );
                setSelectedInterventionId(null);
              }}
              onCancelProvisional={(id) => {
                setInterventionsState((s) => s.filter((it) => it.id !== id));
                setSelectedInterventionId(null);
              }}
              onDeleteIntervention={(id) => {
                setInterventionsState((s) => s.filter((it) => it.id !== id));
                setSelectedInterventionId(null);
              }}
              onClearSelection={() => setSelectedInterventionId(null)}
              onSelectIntervention={(id) => {
                setSelectedInterventionId(id);
                setPanelOpen(true);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PMChart;
